// Smart booking URL generation with pre-filled search parameters
// No API keys required - uses direct search URLs with parameters

// Format date for URL parameters
const formatDateForUrl = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

// Format city name for URL parameters
const formatCityForUrl = (city) => {
  return encodeURIComponent(city.replace(/,.*/, '').trim());
};

// Extract city from venue name
const extractCityFromVenue = (venue) => {
  // Common venue patterns and their cities
  const venueToCity = {
    'Mercedes-Benz Stadium': 'Atlanta, GA',
    'Jones AT&T Stadium': 'Lubbock, TX',
    'AT&T Stadium': 'Arlington, TX',
    'Lambeau Field': 'Green Bay, WI',
    'Soldier Field': 'Chicago, IL',
    'MetLife Stadium': 'East Rutherford, NJ',
    'Gillette Stadium': 'Foxborough, MA',
    'Hard Rock Stadium': 'Miami Gardens, FL',
    'Raymond James Stadium': 'Tampa, FL',
    'State Farm Stadium': 'Glendale, AZ',
    'Arrowhead Stadium': 'Kansas City, MO',
    'M&T Bank Stadium': 'Baltimore, MD',
    'FirstEnergy Stadium': 'Cleveland, OH',
    'Lucas Oil Stadium': 'Indianapolis, IN',
    'Ford Field': 'Detroit, MI',
    'U.S. Bank Stadium': 'Minneapolis, MN',
    'Caesars Superdome': 'New Orleans, LA',
    'Empower Field at Mile High': 'Denver, CO',
    'Allegiant Stadium': 'Las Vegas, NV',
    'SoFi Stadium': 'Los Angeles, CA',
    'Levi\'s Stadium': 'Santa Clara, CA',
    'CenturyLink Field': 'Seattle, WA',
    'Bryant-Denny Stadium': 'Tuscaloosa, AL',
    'Ohio Stadium': 'Columbus, OH',
    'Sanford Stadium': 'Athens, GA',
    'Darrell K Royal Stadium': 'Austin, TX',
    'Tiger Stadium': 'Baton Rouge, LA',
    'Michigan Stadium': 'Ann Arbor, MI',
    'Beaver Stadium': 'University Park, PA',
    'Memorial Stadium': 'Lincoln, NE',
    'Camp Randall Stadium': 'Madison, WI',
    'Rose Bowl': 'Pasadena, CA',
    'Cotton Bowl': 'Dallas, TX',
    'Orange Bowl': 'Miami, FL',
    'Sugar Bowl': 'New Orleans, LA',
    'Gaylord Family Stadium': 'Norman, OK',
    'Memorial Stadium Clemson': 'Clemson, SC',
    'Kyle Field': 'College Station, TX',
    'Ben Hill Griffin Stadium': 'Gainesville, FL',
    'Doak Campbell Stadium': 'Tallahassee, FL',
    'Jordan-Hare Stadium': 'Auburn, AL',
    'Neyland Stadium': 'Knoxville, TN',
    'Williams-Brice Stadium': 'Columbia, SC',
    'Vaught-Hemingway Stadium': 'Oxford, MS',
    'Davis Wade Stadium': 'Starkville, MS',
    'Reynolds Razorback Stadium': 'Fayetteville, AR',
    'Donald W. Reynolds Razorback Stadium': 'Fayetteville, AR',
    'Faurot Field': 'Columbia, MO',
    'Memorial Stadium': 'Lawrence, KS',
    'Bill Snyder Family Stadium': 'Manhattan, KS',
    'Boone Pickens Stadium': 'Stillwater, OK',
    'Amon G. Carter Stadium': 'Fort Worth, TX',
    'McLane Stadium': 'Waco, TX',
    'Alamodome': 'San Antonio, TX',
    'Sun Devil Stadium': 'Tempe, AZ',
    'Folsom Field': 'Boulder, CO',
    'Rice-Eccles Stadium': 'Salt Lake City, UT',
    'LaVell Edwards Stadium': 'Provo, UT',
    'Albertsons Stadium': 'Boise, ID',
    'Martin Stadium': 'Pullman, WA',
    'Husky Stadium': 'Seattle, WA',
    'Autzen Stadium': 'Eugene, OR',
    'Reser Stadium': 'Corvallis, OR',
    'Stanford Stadium': 'Stanford, CA',
    'California Memorial Stadium': 'Berkeley, CA',
    'Rose Bowl': 'Pasadena, CA',
    'Los Angeles Memorial Coliseum': 'Los Angeles, CA',
    'Arizona Stadium': 'Tucson, AZ',
    'Arizona State Sun Devil Stadium': 'Tempe, AZ'
  };

  // Check exact matches first
  if (venueToCity[venue]) {
    return venueToCity[venue];
  }

  // Try partial matches (check for exact venue name match first)
  for (const [venueName, city] of Object.entries(venueToCity)) {
    if (venue.includes(venueName)) {
      return city;
    }
  }

  // Fallback: extract from venue name patterns
  const cityMatches = venue.match(/,\s*([A-Z]{2})/);
  if (cityMatches) {
    const stateCode = cityMatches[1];
    const cityPart = venue.split(',')[0].trim();
    return `${cityPart}, ${stateCode}`;
  }

  // Default fallback
  return venue;
};

export const generateTicketUrl = (game, teamName) => {
  const { date, opponent, venue } = game;
  
  // StubHub with proper 40 Bags affiliate tracking
  const eventName = encodeURIComponent(`${teamName} vs ${opponent}`);
  const venueEncoded = encodeURIComponent(venue);
  const dateFormatted = formatDateForUrl(date);
  
  // Use provided StubHub affiliate link base (no deep param support), fall back to search query in URL hash
  return `https://stubhub.prf.hn/l/W4EJzJX#q=${eventName}&date=${dateFormatted}&venue=${venueEncoded}`;
};

export const generateHotelUrl = (game, teamName) => {
  const { date } = game;
  const city = formatCityForUrl(extractCityFromVenue(game.venue));
  
  // Check-in on game day, check-out next day
  const checkinDate = formatDateForUrl(date);
  const checkoutDate = new Date(date);
  checkoutDate.setDate(checkoutDate.getDate() + 1);
  const checkoutFormatted = formatDateForUrl(checkoutDate.toISOString());
  
  // Hotels.com affiliate landing, append context via hash
  return `https://www.hotels.com/affiliates/hotelscom-home.VsBKzRM#destination=${city}&start=${checkinDate}&end=${checkoutFormatted}`;
};

export const generateFlightUrl = (game, teamName, userLocation = '') => {
  const { date, venue } = game;
  const destinationCity = formatCityForUrl(extractCityFromVenue(venue));
  
  // Flight date (day before game)
  const flightDate = new Date(date);
  flightDate.setDate(flightDate.getDate() - 1);
  const departDate = formatDateForUrl(flightDate.toISOString());
  
  // Return flight (day after game)
  const returnDate = new Date(date);
  returnDate.setDate(returnDate.getDate() + 1);
  const returnFormatted = formatDateForUrl(returnDate.toISOString());
  
  // Use user location if provided, otherwise open general search
  const origin = userLocation ? formatCityForUrl(userLocation) : '';
  
  // Expedia via provided affiliate path, append context via hash
  if (origin) {
    return `https://expedia.com/affiliate/QloXX5p#trip=roundtrip&from=${origin}&to=${destinationCity}&depart=${departDate}&return=${returnFormatted}`;
  } else {
    return `https://expedia.com/affiliate/QloXX5p#trip=roundtrip&to=${destinationCity}&depart=${departDate}&return=${returnFormatted}`;
  }
};

export const generateRestaurantUrl = (game) => {
  const fullLocation = extractCityFromVenue(game.venue);
  const gameDate = formatDateForUrl(game.date);
  
  // Search specifically for sports bars and restaurants near the stadium
  // This will show sports bars, pubs, and restaurants that are likely to show the game
  const searchQuery = `sports bars near ${game.venue} ${fullLocation}`;
  
  return `https://www.google.com/maps/search/${encodeURIComponent(searchQuery)}`;
};


// Alternative restaurant booking service as fallback
export const generateResyRestaurantUrl = (game) => {
  const city = formatCityForUrl(extractCityFromVenue(game.venue));
  const gameDate = formatDateForUrl(game.date);
  
  // Resy.com as alternative restaurant booking service
  const locationQuery = city.includes(',') ? city : `${city}, TX`;
  
  return `https://resy.com/cities/${city.toLowerCase().replace(/\s+/g, '-')}?date=${gameDate}&time=19:00&covers=2`;
};

// Google Maps restaurant search as ultimate fallback
export const generateGoogleMapsRestaurantUrl = (game) => {
  const city = formatCityForUrl(extractCityFromVenue(game.venue));
  const locationQuery = city.includes(',') ? city : `${city}, TX`;
  
  // Google Maps search for restaurants in the specific city
  return `https://www.google.com/maps/search/restaurants+in+${encodeURIComponent(locationQuery)}`;
};

export const generateRentalCarUrl = (game) => {
  const city = formatCityForUrl(extractCityFromVenue(game.venue));
  const pickupDate = formatDateForUrl(game.date);
  
  // Return car next day
  const returnDate = new Date(game.date);
  returnDate.setDate(returnDate.getDate() + 1);
  const returnFormatted = formatDateForUrl(returnDate.toISOString());
  
  // Enterprise with smart parameters
  return `https://www.enterprise.com/en/car-rental/locations.html?pickUpLocation=${city}&pickUpDate=${pickupDate}&dropOffDate=${returnFormatted}&pickUpTime=10:00&dropOffTime=10:00`;
};

// Get airport code for a city (for better flight searches)
export const getAirportCode = (city) => {
  const airportCodes = {
    'Atlanta': 'ATL',
    'Dallas': 'DFW',
    'Arlington': 'DFW',
    'Green Bay': 'GRB',
    'Chicago': 'ORD',
    'New York': 'LGA',
    'East Rutherford': 'LGA',
    'Boston': 'BOS',
    'Foxborough': 'BOS',
    'Miami': 'MIA',
    'Tampa': 'TPA',
    'Phoenix': 'PHX',
    'Glendale': 'PHX',
    'Kansas City': 'MCI',
    'Baltimore': 'BWI',
    'Cleveland': 'CLE',
    'Indianapolis': 'IND',
    'Detroit': 'DTW',
    'Minneapolis': 'MSP',
    'New Orleans': 'MSY',
    'Denver': 'DEN',
    'Las Vegas': 'LAS',
    'Los Angeles': 'LAX',
    'San Francisco': 'SFO',
    'Santa Clara': 'SFO',
    'Seattle': 'SEA',
    'Tuscaloosa': 'BHM',
    'Columbus': 'CMH',
    'Athens': 'ATL',
    'Austin': 'AUS',
    'Baton Rouge': 'BTR',
    'Ann Arbor': 'DTW',
    'University Park': 'MDT',
    'Lincoln': 'LNK',
    'Madison': 'MSN',
    'Pasadena': 'LAX'
  };
  
  const cityName = city.split(',')[0].trim();
  return airportCodes[cityName] || cityName;
};

// Additional ticket options to match 40 Bags partners
export const generateTicketmasterUrl = (game, teamName) => {
  const { date, opponent, venue } = game;
  const eventName = encodeURIComponent(`${teamName} vs ${opponent}`);
  // Use evyy affiliate with encoded deep link to Ticketmaster search
  const deep = encodeURIComponent(`https://www.ticketmaster.com/search?q=${eventName}`);
  return `https://ticketmaster.evyy.net/c/4976406/264167/4272?u=${deep}`;
};

export const generateVividSeatsUrl = (game, teamName) => {
  const { date, opponent, venue } = game;
  const eventName = encodeURIComponent(`${teamName} vs ${opponent}`);
  const dateFormatted = formatDateForUrl(date);
  return `https://vivid-seats.pxf.io/Vm3V23#q=${eventName}&date=${dateFormatted}`;
};

// Additional travel options to match 40 Bags partners
export const generateTravelocityUrl = (game, teamName, userLocation = '') => {
  const { date, venue } = game;
  const destinationCity = formatCityForUrl(extractCityFromVenue(venue));
  const flightDate = new Date(date);
  flightDate.setDate(flightDate.getDate() - 1);
  const departDate = formatDateForUrl(flightDate.toISOString());
  const returnDate = new Date(date);
  returnDate.setDate(returnDate.getDate() + 1);
  const returnFormatted = formatDateForUrl(returnDate.toISOString());
  
  const origin = userLocation ? formatCityForUrl(userLocation) : '';
  
  const base = 'https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704';
  return `${base}#trip=roundtrip&from=${origin || 'any'}&to=${destinationCity}&depart=${departDate}&return=${returnFormatted}`;
};

export const generateVRBOUrl = (game, teamName) => {
  const { date } = game;
  const city = formatCityForUrl(extractCityFromVenue(game.venue));
  const checkinDate = formatDateForUrl(date);
  const checkoutDate = new Date(date);
  checkoutDate.setDate(checkoutDate.getDate() + 1);
  const checkoutFormatted = formatDateForUrl(checkoutDate.toISOString());
  
  return `https://vrbo.com/affiliate/oWiAOm8#city=${city}&arrival=${checkinDate}&departure=${checkoutFormatted}`;
};

// Optional: Booking.com via Awin affiliate
export const generateBookingDotComUrl = (game) => {
  const city = formatCityForUrl(extractCityFromVenue(game.venue));
  const checkinDate = formatDateForUrl(game.date);
  const checkoutDate = new Date(game.date);
  checkoutDate.setDate(checkoutDate.getDate() + 1);
  const checkoutFormatted = formatDateForUrl(checkoutDate.toISOString());
  const deep = encodeURIComponent(`https://www.booking.com/searchresults.html?ss=${city}&checkin=${checkinDate}&checkout=${checkoutFormatted}`);
  return `http://www.awin1.com/awclick.php?mid=6776&id=1499214&clickref=40bags&awinaffid=40bags&ued=${deep}`;
};

export const generateSmartBookingUrls = (game, teamName, userLocation = '') => {
  return {
    // Primary ticket options
    tickets: generateTicketUrl(game, teamName),
    ticketmaster: generateTicketmasterUrl(game, teamName),
    vividseats: generateVividSeatsUrl(game, teamName),
    
    // Primary travel options  
    hotels: generateHotelUrl(game, teamName),
    flights: generateFlightUrl(game, teamName, userLocation),
    travelocity: generateTravelocityUrl(game, teamName, userLocation),
    vrbo: generateVRBOUrl(game, teamName),
    
    // Additional options
    restaurants: generateRestaurantUrl(game),
    rentalCars: generateRentalCarUrl(game)
  };
}; 